import React from 'react';
import styled from 'styled-components';
import { Controller, UseFormReturn } from 'react-hook-form';
import { FormControl, InputLabel, MenuItem, Select } from '@material-ui/core';
import { StyledPaperContainer, StyledSectionTitle } from '../../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { ColumnWithGap } from '../../../../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';
import { ClinicCondition, ClinicWithConditionsAndPromo } from '../../../types';
import useClinicById from '../../hooks/useClinicById';

interface ClinicSpotlightSelectorProps {
  form: UseFormReturn<ClinicWithConditionsAndPromo>;
  clinicId: string;
}

// Writes spotlight_condition_id via the clinic PUT. Options (conditions with a spotlight row) come
// from server truth (useClinicById), so they reflect rows added/removed via the spotlight endpoint.
function ClinicSpotlightSelector({ form, clinicId }: ClinicSpotlightSelectorProps) {
  const { clinic } = useClinicById(clinicId);
  const conditions = clinic?.clinic_conditions ?? [];
  const eligible = conditions.filter((c) => Boolean(c.spotlight_id || c.spotlight));

  return (
    <StyledPaperContainer>
      <ColumnWithGap>
        <StyledSectionTitle>Spotlight condition</StyledSectionTitle>
        {eligible.length === 0 ? (
          <Notice>
            No conditions have spotlight content yet. Add a spotlight to a condition (in its editor on the
            Conditions tab) to make it selectable here.
          </Notice>
        ) : (
          <Controller
            control={form.control}
            name="spotlight_condition_id"
            render={({ field }) => {
              const value = field.value || '';
              const selectedIsEligible = !value || eligible.some((c) => c._id === value);

              return (
                <FormControl fullWidth>
                  <InputLabel>Featured spotlight</InputLabel>
                  <Select
                    value={value}
                    displayEmpty
                    onChange={(event) => field.onChange(event.target.value === '' ? null : event.target.value)}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {eligible.map((condition) => (
                      <MenuItem key={condition._id} value={condition._id}>
                        {conditionLabel(condition)}
                      </MenuItem>
                    ))}
                  </Select>
                  {!selectedIsEligible && (
                    <Warning>
                      The selected condition no longer has spotlight content — choose another condition or None.
                    </Warning>
                  )}
                </FormControl>
              );
            }}
          />
        )}
      </ColumnWithGap>
    </StyledPaperContainer>
  );
}

function conditionLabel(condition: ClinicCondition) {
  return condition.condition?.name || condition.spotlight?.headline || condition.condition_id || condition._id;
}

const Notice = styled.div`
  padding: 12px 16px;
  background-color: #fff9c4;
  border-left: 4px solid #fbc02d;
  color: #424242;
  border-radius: 4px;
`;

const Warning = styled.div`
  margin-top: 8px;
  color: #c62828;
`;

export default ClinicSpotlightSelector;
